import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@volca/ui/components/ui/dialog";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { AccessHeader } from "./access-header";
import { AddUser } from "./add-user";
import { DraftControls } from "./draft-controls";
import { PeopleTable } from "./people-table";
import { PersonAccessDrawer } from "./person-access-drawer";
import { TeamsTable } from "./teams-table";
import type { AccessAssignment, WorkbookAccessSettings } from "./types";
import { useWorkbookAccessChanges } from "./use-workbook-access-changes";
import { replacePersonAssignments } from "./utils";

interface WorkbookAccessEditorProps {
  businessId: string;
  settings: WorkbookAccessSettings;
  initialPersonId?: string | null;
}

export const WorkbookAccessEditor = ({
  businessId,
  settings,
  initialPersonId,
}: WorkbookAccessEditorProps) => {
  const { working, save, saveWorking, saveKey } = useWorkbookAccessChanges(
    businessId,
    settings,
  );
  const [editingId, setEditingId] = useState<string | null>(
    initialPersonId ?? null,
  );
  const [tab, setTab] = useState("people");
  const [ownerHelp, setOwnerHelp] = useState(false);
  const disabled = save.isPending || Boolean(settings.draft?.stale);
  const editingPerson = settings.users.find((user) => user.id === editingId);

  const savePerson = async (userId: string, rows: AccessAssignment[]) => {
    const saved = await saveWorking(
      replacePersonAssignments(working, userId, rows),
    );
    // Close only the drawer that was saved.
    if (saved) {
      setEditingId((current) => (current === userId ? null : current));
    }
  };

  return (
    <SimpleSidebarLayout
      open={editingPerson != null}
      onOpenChange={(open) => {
        if (open || save.isPending) return;
        setEditingId(null);
      }}
      sidebarWidth="min(480px, 100vw)"
      className="h-full!"
    >
      <SimpleSidebarContent
        removeScrollArea
        className="flex flex-col motion-reduce:transition-none!"
      >
        <Stack align="start" gap={6} className="h-full min-h-0 w-full">
          <AccessHeader
            actions={
              <Stack direction="row" gap={2} align="center">
                <Button
                  variant="outline"
                  nativeButton={false}
                  render={
                    <Link to={`/business-settings/${businessId}/users`} />
                  }
                >
                  Manage users
                </Button>
                <AddUser
                  businessId={businessId}
                  onCreated={(id) => {
                    setTab("people");
                    setEditingId(id);
                  }}
                />
              </Stack>
            }
          />
          <DraftControls
            businessId={businessId}
            settings={settings}
            disabled={save.isPending}
            onDiscarded={() => setEditingId(null)}
          />
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="min-h-0 w-full flex-1 gap-0"
          >
            {/* Both tabs stay mounted, so a sheet name that failed to save is still
                there after a look at People. */}
            <div className="border-b">
              <TabsList variant="line">
                <TabsTrigger value="people">People</TabsTrigger>
                <TabsTrigger value="teams">Sheets and teams</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value="people"
              keepMounted
              className="min-h-0 flex-1 overflow-auto pt-4 data-hidden:hidden"
            >
              <PeopleTable
                settings={settings}
                working={working}
                selectedId={editingId}
                onSelect={setEditingId}
                disabled={disabled}
              />
            </TabsContent>
            <TabsContent
              value="teams"
              keepMounted
              className="min-h-0 flex-1 overflow-auto pt-4 data-hidden:hidden"
            >
              <TeamsTable
                settings={settings}
                working={working}
                saving={disabled}
                onChange={saveKey}
              />
            </TabsContent>
          </Tabs>
        </Stack>
      </SimpleSidebarContent>

      {editingPerson && (
        <PersonAccessDrawer
          key={editingPerson.id}
          businessId={businessId}
          person={editingPerson}
          settings={settings}
          working={working}
          saving={disabled}
          onSave={(rows) => savePerson(editingPerson.id, rows)}
          onCancel={() => setEditingId(null)}
          saveLabel="Save draft"
          onTransfer={() => setOwnerHelp(true)}
        />
      )}

      <Dialog open={ownerHelp} onOpenChange={setOwnerHelp}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keep a manager with full access</DialogTitle>
            <DialogDescription>
              First, set another business user's role to Manager in Users. Then
              give that manager whole-workbook Edit access in this draft. You
              can then change the current manager's access and apply all changes
              together.
            </DialogDescription>
          </DialogHeader>
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link to={`/business-settings/${businessId}/users`} />}
          >
            Manage users
          </Button>
        </DialogContent>
      </Dialog>
    </SimpleSidebarLayout>
  );
};
